import * as React from 'react';
import { Alert, Autocomplete, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { getMarketingPushMatch, getSearchMatches } from '../../Api';
import type { PushMatchDetails } from '../../models/dtos/MarketingPushDto';
import { formatDateTime } from '../polls/PollShared';

type Option = { id:number; label:string };
const stateNames:Record<number,string>={1:'Upcoming',2:'Live',3:'Finished',4:'Processing',5:'Cancelled'};
const matchLabel=(m:PushMatchDetails)=>`#${m.id} · ${m.homeTeam} vs ${m.awayTeam}`;

export default function PushMatchPicker({matchId,onIdChange,onResolved,onConfirmed,validationError}: {
    matchId:string; validationError?:string; onIdChange:(id:string)=>void; onResolved:(match:PushMatchDetails)=>void; onConfirmed:(id:string|null)=>void;
}) {
    const [input,setInput]=React.useState('');
    const [query,setQuery]=React.useState('');
    const [options,setOptions]=React.useState<Option[]>([]);
    const [searching,setSearching]=React.useState(false);
    const [searchError,setSearchError]=React.useState('');
    const [details,setDetails]=React.useState<PushMatchDetails|null>(null);
    const [verifying,setVerifying]=React.useState(false);
    const [error,setError]=React.useState('');
    const [retry,setRetry]=React.useState(0);
    React.useEffect(()=>{
        let live=true;setDetails(null);setError('');onConfirmed(null);setVerifying(false);
        if(!matchId)return;
        const id=Number(matchId);
        if(!Number.isSafeInteger(id)||id<=0){setError('Choose a valid match from the search results.');return;}
        setVerifying(true);setInput(`Match #${id}`);
        getMarketingPushMatch(id).then(match=>{
            if(!live)return;
            if(!match||match.id!==id)throw new Error('Match could not be verified. Search for another match or retry.');
            setDetails(match);setInput(matchLabel(match));setQuery('');onResolved(match);
            onConfirmed(match.isBadMatch?null:String(match.id));
        }).catch(e=>{if(live)setError(e.message||'Match could not be verified.');}).finally(()=>{if(live)setVerifying(false);});
        return()=>{live=false;};
    },[matchId,retry,onResolved,onConfirmed]);
    React.useEffect(()=>{
        let live=true;setOptions([]);setSearchError('');
        const term=query.trim();
        if(term.length<2&&!/^\d+$/.test(term)){setSearching(false);return;}
        setSearching(true);
        const timer=window.setTimeout(async()=>{
            try {
                const matches=await getSearchMatches(term);if(!live)return;
                if(!Array.isArray(matches))throw new Error('Match search is unavailable.');
                const seen=new Set<number>();
                setOptions(matches.filter(m=>{if(!Number.isSafeInteger(m.betupId)||m.betupId<=0||seen.has(m.betupId))return false;seen.add(m.betupId);return true;}).slice(0,20).map(m=>({id:m.betupId,
                    label:`#${m.betupId} · ${m.homeTeam?.name??'Unknown home team'} vs ${m.awayTeam?.name??'Unknown away team'} · ${m.sport?.name??'Sport unknown'} · ${formatDateTime(m.date)}`})));
            }catch(e){if(live)setSearchError(e instanceof Error?e.message:'Match search is unavailable.');}
            finally{if(live)setSearching(false);}
        },300);
        return()=>{live=false;window.clearTimeout(timer);};
    },[query]);
    const selected=details&&String(details.id)===matchId?{id:details.id,label:matchLabel(details)}:null;
    return <Stack spacing={1}>
        <Autocomplete options={options} value={selected} inputValue={input} filterOptions={items=>items}
            isOptionEqualToValue={(a,b)=>a.id===b.id} getOptionLabel={o=>o.label} loading={searching||verifying}
            noOptionsText={query.trim()?'No matches found':'Search by team, league or match ID'}
            onInputChange={(_,value,reason)=>{
                if(reason==='reset')return;
                setInput(value);setQuery(value);
                if(matchId){onConfirmed(null);onIdChange('');}
            }}
            onChange={(_,option)=>{setQuery('');setOptions([]);setInput(option?.label??'');onConfirmed(null);onIdChange(option?String(option.id):'');}}
            renderInput={params=><TextField {...params} label="Match" required placeholder="Search by team, league or match ID"
                helperText={validationError??'Choose a search result to verify its current match details.'} error={!!error||!!validationError}
                InputProps={{...params.InputProps,endAdornment:<>{(searching||verifying)&&<CircularProgress size={18}/>} {params.InputProps.endAdornment}</>}}/>}/>
        {verifying&&<Typography variant="body2">Verifying match…</Typography>}
        {searchError&&<Alert severity="warning">{searchError}</Alert>}
        {error&&<Alert severity="error" action={<Button color="inherit" onClick={()=>setRetry(v=>v+1)}>Retry verification</Button>}>{error}</Alert>}
        {selected&&details&&<Alert severity={details.isBadMatch?'error':details.state===5?'warning':'success'}>
            <Typography fontWeight="bold">{details.isBadMatch?'Match flagged as invalid':'Match verified'}: {details.homeTeam} vs {details.awayTeam}</Typography>
            <Typography variant="body2">ID {details.id} · {details.sport??'Sport unknown'} · {stateNames[details.state]??`State ${details.state}`} · Kickoff: {formatDateTime(details.startsAt)} · Current score: {details.scoreHome}–{details.scoreAway}</Typography>
            {details.isBadMatch&&<Typography variant="body2">Choose another match before sending.</Typography>}
        </Alert>}
    </Stack>;
}
